import axios, { AxiosRequestConfig } from "axios";
import { useAuthStore } from "../stores/auth";
import { currentLocale } from "@/i18n";
import { requestStarted, requestFinished } from "./loading";


const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL, // global API URL
  // withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const auth = useAuthStore();
  config.headers["Accept-Language"] = currentLocale();
  // A caller may pass its own Authorization (e.g. the parent account's token
  // while a sub account is active); otherwise use the active session.
  if (auth.token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${auth.token}`;
  }
  // GET requests to the same URL are made under different accounts (e.g. the
  // "login as staff" flow, where several sessions hit /fecthPerms with an
  // identical URL). The browser HTTP cache keys on URL, not on the
  // Authorization header, so without this it can silently serve a previous
  // user's cached response. Force revalidation on every request.
  config.headers["Cache-Control"] = "no-cache";
  config.headers["Pragma"] = "no-cache";
  // The default JSON Content-Type makes axios serialize FormData to JSON,
  // which drops File entries. Let the browser set multipart + boundary.
  if (config.data instanceof FormData) {
    config.headers.delete("Content-Type");
  }
  return config;
});


// A page often fires several requests at once; when the token expires they all
// come back 401. Redirect only once instead of once per request.
let redirectingToLogin = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !redirectingToLogin) {
      const auth = useAuthStore();
      auth.clearAuth();
      if (window.location.pathname !== "/login") {
        redirectingToLogin = true;
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);
// Top progress bar (utils/loading.ts): count in-flight requests. The flag on
// the config makes sure every started request is finished exactly once.
type TrackedConfig = { __uiLoading?: boolean };
function finishTracked(config?: TrackedConfig) {
  if (config?.__uiLoading) {
    config.__uiLoading = false;
    requestFinished();
  }
}
api.interceptors.request.use((config) => {
  (config as TrackedConfig).__uiLoading = true;
  requestStarted();
  return config;
});
api.interceptors.response.use(
  (response) => {
    finishTracked(response.config as TrackedConfig);
    return response;
  },
  (error) => {
    finishTracked(error?.config as TrackedConfig);
    return Promise.reject(error);
  }
);

// Reference data (statuses, cities, zones...) is requested by many pages and
// changes rarely. Share one in-flight request and keep the result for a few
// minutes so navigating between lists doesn't refetch it every time.
// The key includes the token: accounts never see each other's cached data.
const LOOKUP_TTL_MS = 5 * 60 * 1000;
const lookupCache = new Map<string, { at: number; promise: Promise<any> }>();

export function cachedGet<T = any>(url: string, config: AxiosRequestConfig = {}): Promise<{ data: T }> {
  const auth = useAuthStore();
  const key = `${auth.token || ""}|${url}|${JSON.stringify(config.params || {})}`;
  let entry = lookupCache.get(key);
  if (!entry || Date.now() - entry.at > LOOKUP_TTL_MS) {
    const promise = api.get(url, config).then((res) => res.data);
    const created = (entry = { at: Date.now(), promise });
    lookupCache.set(key, created);
    // a failed request must not be served again
    promise.catch(() => {
      if (lookupCache.get(key) === created) lookupCache.delete(key);
    });
  }
  // callers mutate lists (unshift "Tous"...): give each one its own copy
  return entry.promise.then((data) => ({ data: data == null ? data : JSON.parse(JSON.stringify(data)) }));
}

export function clearLookupCache(): void {
  lookupCache.clear();
}

export default api;
